import { getPlatform } from '../platform';
import type { AuthInitResponse, AuthRefreshResponse } from './types';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.awardy.ru';

// Module-private auth state — never logged.
let accessToken: string | null = null;
let refreshToken: string | null = null;
let sessionId: string | null = null;
let bootstrapInflight: Promise<void> | null = null;
let refreshInflight: Promise<void> | null = null;

// FTR-1115: cache onboarding_completed from bootstrap response for synchronous gate read in FirstLaunchGate.
let onboardingCompleted: boolean | null = null;
export function getCachedOnboardingCompleted(): boolean | null {
  return onboardingCompleted;
}

export function setOnboardingCompleted(value: boolean): void {
  onboardingCompleted = value;
}

// BUG-1144: auth-readiness observers — lets useQuery gates (creator wizard steps)
// react when accessToken flips null↔set. A snapshot alone (isAuthReady) does not
// re-render React; subscribers do. Mirrors the module-level cache pattern above
// (getCachedOnboardingCompleted). Set<callback>, fired on every readiness change.
const authReadyListeners = new Set<() => void>();

function notifyAuthReady(): void {
  // Snapshot-and-iterate: callbacks may unsubscribe during notify.
  for (const cb of [...authReadyListeners]) cb();
}

/** Synchronous snapshot: true once a JWT is held (post-bootstrap/refresh). */
export function isAuthReady(): boolean {
  return accessToken !== null;
}

/**
 * Subscribe to auth-readiness changes. Returns an unsubscribe fn.
 * Used by useAuthReady() (creator wizard) so `enabled` gates re-evaluate
 * when the cold-deeplink bootstrap finishes after the step already mounted.
 */
export function subscribeAuthReady(cb: () => void): () => void {
  authReadyListeners.add(cb);
  return () => {
    authReadyListeners.delete(cb);
  };
}

export async function ensureBootstrapped(): Promise<void> {
  try {
    await bootstrap();
  } catch {
    // Fail-open: bootstrap error must NOT block the app with infinite spinner.
    // Backend guard (BUG-1132 BE-1) provides defense-in-depth.
  }
}

async function bootstrap(): Promise<void> {
  if (accessToken !== null) return;
  if (bootstrapInflight) return bootstrapInflight;
  bootstrapInflight = (async () => {
    const platform = await getPlatform();
    if (platform.apiPlatform == null) {
      throw new Error('No platform for auth bootstrap');
    }
    const res = await fetch(`${API_BASE}/api/v2/buyer/auth/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platform: platform.apiPlatform,
        init_data: platform.getInitData(),
      }),
    });
    if (!res.ok) {
      accessToken = refreshToken = sessionId = null;
      const err = new Error(`auth/init ${res.status}`) as Error & { status: number };
      err.status = res.status;
      throw err;
    }
    const data = (await res.json()) as AuthInitResponse;
    accessToken = data.access_token;
    refreshToken = data.refresh_token;
    sessionId = data.session_id;
    onboardingCompleted = data.onboarding_completed;
    notifyAuthReady();
  })();
  try {
    await bootstrapInflight;
  } finally {
    bootstrapInflight = null;
  }
}

async function refresh(): Promise<void> {
  if (refreshInflight) return refreshInflight;
  if (refreshToken === null) {
    throw new Error('No refresh token');
  }
  const tokenForRequest = refreshToken;
  refreshInflight = (async () => {
    const res = await fetch(`${API_BASE}/api/v2/buyer/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: tokenForRequest }),
    });
    if (!res.ok) {
      accessToken = refreshToken = sessionId = null;
      const err = new Error(`auth/refresh ${res.status}`) as Error & { status: number };
      err.status = res.status;
      throw err;
    }
    const data = (await res.json()) as AuthRefreshResponse;
    accessToken = data.access_token;
    refreshToken = data.refresh_token;
    notifyAuthReady();
  })();
  try {
    await refreshInflight;
  } finally {
    refreshInflight = null;
  }
}

async function sendOnce(path: string, init: RequestInit | undefined, apiPlatformCode: string): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${accessToken}`,
    'X-Client-Platform': apiPlatformCode,
    ...(init?.headers as Record<string, string> | undefined),
  };
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  if (accessToken === null) {
    await bootstrap();
  }
  const platform = await getPlatform();
  const apiPlatformCode = platform.apiPlatform ?? '';

  let res = await sendOnce(path, init, apiPlatformCode);

  if (res.status === 401) {
    if (refreshToken !== null) {
      try {
        await refresh();
      } catch {
        // refresh failed -- fall through to throw below
      }
    } else {
      accessToken = null;
      try {
        await bootstrap();
      } catch {
        // bootstrap failed -- fall through to throw below
      }
    }
    if (accessToken !== null) {
      res = await sendOnce(path, init, apiPlatformCode);
    }
  }

  if (!res.ok) {
    // Read the body ONCE — reused for both err.message (backward-compat) and the
    // structured userMessage/code parse below. A Response body can only be read once.
    const bodyText = await res.text();
    const err = new Error(`API ${res.status}: ${bodyText}`) as Error & {
      status: number;
      retryAfter: number | null;
      // BUG-1153: structured user-facing reason from the backend error envelope.
      // Additive only — err.message format is unchanged (WalletScreen/RequisitesScreen/
      // ProductScreen string-match it). The Cyrillic guard that decides whether to SHOW
      // userMessage lives at the call site (CitiesStep) — client.ts stays a generic transport.
      userMessage?: string;
      code?: string;
    };
    err.status = res.status;
    // FTR-1106: expose Retry-After (seconds) for 429 surface (creator/verify_apply rate-limit).
    // Backward-compatible: existing callers ignore the new field.
    const retryHeader = res.headers.get('Retry-After');
    err.retryAfter = retryHeader && /^\d+$/.test(retryHeader) ? parseInt(retryHeader, 10) : null;
    // BUG-1153: extract the backend's user-facing detail.message (if any).
    // FastAPI HTTPException serialises as {"detail": <str | {code, message} | [Pydantic list]>}.
    // Non-JSON bodies and Pydantic list-detail leave userMessage undefined.
    // Never put raw JSON / machine codes into userMessage.
    try {
      const parsed = JSON.parse(bodyText) as { detail?: unknown };
      const detail = parsed.detail;
      if (typeof detail === 'string') {
        err.userMessage = detail;
      } else if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const d = detail as { message?: unknown; code?: unknown };
        if (typeof d.message === 'string') err.userMessage = d.message;
        if (typeof d.code === 'string') err.code = d.code;
      }
      // Array detail (Pydantic validation list) → leave userMessage/code unset.
    } catch {
      // Body was not JSON (e.g. "Unprocessable Entity") — leave userMessage/code unset.
    }
    throw err;
  }
  return res.json() as Promise<T>;
}

// Test seam — for unit tests only.
export const _internals = {
  reset(): void {
    accessToken = null;
    refreshToken = null;
    sessionId = null;
    bootstrapInflight = null;
    refreshInflight = null;
    onboardingCompleted = null;
    notifyAuthReady();
  },
  getState(): { accessToken: string | null; refreshToken: string | null; sessionId: string | null } {
    return { accessToken, refreshToken, sessionId };
  },
};
